import { Box } from '@mui/material'
import { lighten, styled } from '@mui/material/styles'
import type {
  GridCallbackDetails,
  GridEventListener,
  GridRowSelectionModel,
} from '@mui/x-data-grid'
import {
  DataGridPro,
  type GridColumnOrderChangeParams,
  type GridColumnVisibilityModel,
  type GridPaginationModel,
  type GridRowClassNameParams,
  type GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import { useCallback, useEffect, useMemo } from 'react'
import { SEARCH_RESULTS_PER_PAGE } from '@/features/search/constants'
import { useSearch } from '@/features/search/hooks/useSearch'
import { defaultUserSearchPreferences } from '@/features/userSettings/defaults'
import { mapLocalToDataGrid } from '@/features/userSettings/mapSearchSettings'
import { useUserPreferences } from '@/features/userSettings/useUserPreferences'
import { useAppSelector } from '@/redux/hooks'
import type { OrderBy } from '../../types'
import { NoResultsMessage } from '../NoResultsMessage'
import { CustomColumnsManagement } from './CustomColumnsManagement'
import CustomPagination from './CustomPagination'
import { columnGroups, useColumns } from './columns'

const StyledDataGridPro = styled(DataGridPro)(({ theme }) => {
  const oddBackground = theme.palette.common.white
  const evenBackground = '#f7f7f7'
  const hoverBackground = lighten(theme.palette.info.main, 0.85)
  const selectedBackground = lighten(theme.palette.info.main, 0.8)
  const selectedHoverBackground = lighten(theme.palette.info.main, 0.75)

  return {
    '& .MuiDataGrid-columnHeader--pinnedLeft, & .MuiDataGrid-columnHeader--pinnedRight':
      {
        backgroundColor: theme.palette.background.default,
      },
    // Base striping
    '& .MuiDataGrid-row.odd': {
      backgroundColor: oddBackground,
      '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
        backgroundColor: oddBackground,
      },
    },
    '& .MuiDataGrid-row.even': {
      backgroundColor: evenBackground,
      '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
        backgroundColor: evenBackground,
      },
    },
    // Hover state
    '& .MuiDataGrid-row.odd:hover, & .MuiDataGrid-row.odd.Mui-hovered': {
      backgroundColor: `${hoverBackground} !important`,
      '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
        backgroundColor: `${hoverBackground} !important`,
      },
    },
    '& .MuiDataGrid-row.even:hover, & .MuiDataGrid-row.even.Mui-hovered': {
      backgroundColor: `${hoverBackground} !important`,
      '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
        backgroundColor: `${hoverBackground} !important`,
      },
    },
    // Selected state
    '& .MuiDataGrid-row.odd.Mui-selected': {
      backgroundColor: selectedBackground,
      '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
        backgroundColor: selectedBackground,
      },
    },
    '& .MuiDataGrid-row.even.Mui-selected': {
      backgroundColor: selectedBackground,
      '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
        backgroundColor: selectedBackground,
      },
    },
    // Selected + Hover
    '& .MuiDataGrid-row.odd.Mui-selected:hover, & .MuiDataGrid-row.odd.Mui-selected.Mui-hovered':
      {
        backgroundColor: `${selectedHoverBackground} !important`,
        '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
          backgroundColor: `${selectedHoverBackground} !important`,
        },
      },
    '& .MuiDataGrid-row.even.Mui-selected:hover, & .MuiDataGrid-row.even.Mui-selected.Mui-hovered':
      {
        backgroundColor: `${selectedHoverBackground} !important`,
        '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
          backgroundColor: `${selectedHoverBackground} !important`,
        },
      },
  }
})

export const SearchResultsDataGrid = () => {
  const apiRef = useGridApiRef()
  const columns = useColumns()
  const { searchResults, selectors, updateMetaOptions, updateSelection } =
    useSearch()
  const {
    datagrid: dataGridSettings,
    getRequest: getSearchPrefsRequest,
    updateUserSearchPreferences,
  } = useUserPreferences()
  const searchMetaOptions = useAppSelector(selectors.selectMetaOptions)
  const { rows, details, request } = searchResults

  const selectionModel = useAppSelector(selectors.selectCurrentSelectionRowIds)

  const initialState = useMemo(
    () => dataGridSettings ?? mapLocalToDataGrid(defaultUserSearchPreferences),
    [dataGridSettings],
  )

  useEffect(
    function updateColumnVisibilityOnSettingsLoad() {
      if (!dataGridSettings.columns?.columnVisibilityModel) return

      apiRef.current.setColumnVisibilityModel(
        dataGridSettings.columns.columnVisibilityModel,
      )
    },
    [dataGridSettings.columns?.columnVisibilityModel, apiRef],
  )

  useEffect(
    function updatePageSizeOnSettingsLoad() {
      if (!dataGridSettings.pagination?.paginationModel?.pageSize) return

      apiRef.current.setPageSize(
        dataGridSettings.pagination?.paginationModel?.pageSize,
      )
    },
    [dataGridSettings.pagination?.paginationModel?.pageSize, apiRef],
  )

  useEffect(
    function updateColumnOrderOnSettingsLoad() {
      if (!dataGridSettings.columns?.orderedFields) return
      const { orderedFields } = dataGridSettings.columns

      orderedFields.forEach((fieldName: string, index: number) => {
        apiRef.current.setColumnIndex(fieldName, index)
      })
    },
    [dataGridSettings.columns, apiRef],
  )

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      let nextIdx = model.page * model.pageSize

      if (model.pageSize !== searchMetaOptions.size) {
        if (getSearchPrefsRequest.isSuccess)
          updateUserSearchPreferences({ resultsPerPage: model.pageSize })
        nextIdx = searchMetaOptions.skip
        model.page = Math.floor(nextIdx / model.pageSize)
      }

      updateMetaOptions({ skip: nextIdx, size: model.pageSize })
    },
    [
      searchMetaOptions,
      getSearchPrefsRequest.isSuccess,
      updateMetaOptions,
      updateUserSearchPreferences,
    ],
  )

  const handleSortChange = (model: GridSortModel) => {
    const _model = [...model] as OrderBy[]
    updateMetaOptions({ orderBy: _model })
  }

  const handleColumnOrderChange = (_model: GridColumnOrderChangeParams) => {
    const cols = apiRef.current.getAllColumns().map((c) => c.field)
    if (getSearchPrefsRequest.isSuccess)
      updateUserSearchPreferences({ columnOrder: cols })
  }

  const handleColumnVisibilityModelChange = (
    model: GridColumnVisibilityModel,
  ) => {
    updateUserSearchPreferences({
      columnsToDisplay: model as Record<string, boolean>,
    })
  }

  const handleSelectionChange = useCallback(
    (newModel: GridRowSelectionModel, details: GridCallbackDetails) => {
      const {
        api: { getRow },
      } = details

      const oldSet = new Set(selectionModel)
      const newSet = new Set(newModel)

      const added = newSet.difference(oldSet)
      const removed = oldSet.difference(newSet)

      const rowData = [...added, ...removed].map((row) => getRow(row))

      updateSelection(rowData)
    },
    [selectionModel, updateSelection],
  )

  const handleRowClick: GridEventListener<'rowClick'> = useCallback(
    (_params, event, _details) => {
      event.preventDefault()
    },
    [],
  )

  const handleRowDoubleClick: GridEventListener<'rowDoubleClick'> = useCallback(
    (_params, event, _details) => {
      event.preventDefault()
      console.log('Double Clicked!')
    },
    [],
  )

  return (
    <Box flexGrow={1} position="relative" minHeight={400}>
      <Box position="absolute" top={0} left={0} right={0} bottom={0}>
        <StyledDataGridPro
          disableColumnFilter
          apiRef={apiRef}
          checkboxSelection
          rows={rows}
          loading={request.isFetching}
          columns={columns}
          columnGroupingModel={columnGroups}
          columnHeaderHeight={36}
          columnVisibilityModel={
            dataGridSettings.columns?.columnVisibilityModel
          }
          sortModel={dataGridSettings.sorting?.sortModel}
          initialState={initialState}
          getRowClassName={getRowCssClassName}
          pagination
          paginationMode="server"
          rowCount={details.totalCount}
          pageSizeOptions={SEARCH_RESULTS_PER_PAGE}
          paginationModel={{
            page: Math.floor(searchMetaOptions.skip / searchMetaOptions.size),
            pageSize: searchMetaOptions.size,
          }}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          onSortModelChange={handleSortChange}
          onColumnOrderChange={handleColumnOrderChange}
          onPaginationModelChange={handlePaginationModelChange}
          keepNonExistentRowsSelected
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={handleSelectionChange}
          onRowClick={handleRowClick}
          onRowDoubleClick={handleRowDoubleClick}
          slots={{
            noRowsOverlay: NoResultsMessage,
            pagination: CustomPagination,
            columnsManagement: CustomColumnsManagement,
          }}
        />
      </Box>
    </Box>
  )
}

const getRowCssClassName = (params: GridRowClassNameParams) =>
  params.indexRelativeToCurrentPage % 2 === 0 ? 'odd' : 'even'
